/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  FormGroup,
  Label,
  Button,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  Alert,
  Table
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import SimpleHeader from '../../components/Headers/SimpleHeader';
import SweetAlerts from '../../components/SweetAlerts';
import GInput, { Select } from '../../components/Input';
import client from '../../utils/client';
import { getCountry, isSuperAdmin } from '../../utils/helpers';

const isUserSuperAdmin = isSuperAdmin();

const regexEmail =
  // eslint-disable-next-line max-len
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const defaultExtraProducts = {
  SE: [
    { code: 'ST', name: 'Ställ (Ställ för broschyr och appkort)' },
    { code: 'LH', name: 'Lathund Golfamore klubb (Instruktioner för klubb-admin)' },
    { code: 'GH26K', name: 'Golfamore 2026', onlyAdmin: true },
    { code: 'GH26D', name: 'Golfamore 2026 Digitalt', onlyAdmin: true },
    { code: 'XMASENV', name: 'Julkuvert (Extra)', onlyAdmin: true }
  ],
  DK: [
    { code: 'ST', name: 'Stander (Stativ til brochure og app-kort)' },
    { code: 'LH', name: 'Guide Golfamore Klub (instruktioner til klubadministrator)' },
    { code: 'GH26K', name: 'Golfamore 2026', onlyAdmin: true },
    { code: 'GH26D', name: 'Golfamore 2026 Digitalt', onlyAdmin: true },
    { code: 'XMASENV', name: 'Julkuvert (Extra)', onlyAdmin: true }
  ],
  NO: [
    { code: 'ST', name: 'Ställ (Ställ för broschyr och appkort)' },
    { code: 'LH', name: 'Lathund Golfamore klubb (Instruktioner för klubb-admin)' },
    { code: 'GH26K', name: 'Golfamore 2026', onlyAdmin: true },
    { code: 'GH26D', name: 'Golfamore 2026 Digitalt', onlyAdmin: true },
    { code: 'XMASENV', name: 'Julkuvert (Extra)', onlyAdmin: true }
  ],
  FI: [
    { code: 'ST', name: 'Ställ (Ställ för broschyr och appkort)' },
    { code: 'LH', name: 'Lathund Golfamore klubb (Instruktioner för klubb-admin)' },
    { code: 'GH26K', name: 'Golfamore 2026', onlyAdmin: true },
    { code: 'GH26D', name: 'Golfamore 2026 Digitalt', onlyAdmin: true },
    { code: 'XMASENV', name: 'Julkuvert (Extra)', onlyAdmin: true }
  ],
  EN: [
    { code: 'ST', name: 'Ställ (Ställ för broschyr och appkort)' },
    { code: 'LH', name: 'Lathund Golfamore klubb (Instruktioner för klubb-admin)' },
    { code: 'GH26K', name: 'Golfamore 2026', onlyAdmin: true },
    { code: 'GH26D', name: 'Golfamore 2026 Digitalt', onlyAdmin: true },
    { code: 'XMASENV', name: 'Julkuvert (Extra)', onlyAdmin: true }
  ]
};

const defaultQtyOptions = [25, 50, 75, 100, 125, 150, 175, 200, 225, 250, 275, 300, 310];
const superAdminQtyOptions = Array.from({ length: 300 }, (_, i) => i + 1);
const qtyOptions = isUserSuperAdmin ? superAdminQtyOptions : defaultQtyOptions;

const superAdminPriceOptions = [
  100, 179, 200, 215, 230, 256, 292, 296, 306, 310, 316, 320, 348, 365, 0, 1, 22
];

let extraProducts = [];
let danishPrices = {};

const country = getCountry();

const Order = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [inProgress, setInProgress] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [addressData, setAddressData] = useState({});
  const [product, setProduct] = useState({});
  const [products, setProducts] = useState([]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset
  } = useForm();

  useEffect(() => {
    document.body.classList.add('order');
    return () => document.body.classList.remove('order');
  }, []);

  useEffect(() => {
    (async () => {
      setInProgress(true);

      extraProducts = defaultExtraProducts[country]?.filter(
        (p) => !p.onlyAdmin || isUserSuperAdmin
      );

      const { data: clubInfo } = await client('admin-gc/club-info');
      const { data: prods } = await client('admin-gc/products?for-order=1');

      if (prods) {
        // const tmpProduct = prods.find((p) => p.code === 'GH');
        const tmpProduct = prods[0];

        // console.log('tmpProduct', tmpProduct);

        if (tmpProduct) {
          // For danish golf clubs we offer both DKK and EUR as payment
          if (country === 'DK') {
            danishPrices = {
              EUR: Math.round((tmpProduct.price / 7.4372) * 10) / 10,
              DKK: tmpProduct.price
            };
          }

          setProduct(tmpProduct);
          setValue('productCode', tmpProduct.code);
          setValue('productPrice', country === 'DK' ? '' : tmpProduct.price);
        }

        setProducts(prods);
      }

      setAddressData(clubInfo || {});
      setInProgress(null);
    })();
  }, []);

  const onSubmit = async (formData) => {
    setError(null);
    setSuccess(null);
    setInProgress('save');

    const orderedProduct = products.find((sp) => sp.code === formData.productCode);

    // Special case for danish clubs that can select between DKK and EUR.
    if (country === 'DK' && ['DKK', 'EUR'].includes(formData.productPrice)) {
      formData.productCurrency = formData.productPrice;
      formData.productPrice = danishPrices[formData.productCurrency];
    }

    const { data, error: orderError } = await client('admin-gc/orders', {
      body: { ...formData, product: orderedProduct }
    });

    if (orderError) {
      setError(orderError.message);
    } else {
      setSuccess(data);
    }
    setInProgress(null);
  };

  const clearAll = () => {
    reset(['name', 'email', 'mobile', 'productQty', 'planned']);
    setError(null);
    setSuccess(null);
  };

  // console.log('errors', errors);

  return (
    <>
      <SimpleHeader name={t('order.title', 'Beställ Golfamore')} />

      <Container className="mt--6" fluid>
        <Row>
          <Col>
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">{t('order.sub-title', 'Era uppgifter')}</h3>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col xs="12" sm="4">
                      <h4 className="bold">{t('order.delivery-address', 'Leverans-adress')}</h4>
                      <p>
                        {addressData.name}
                        <br />
                        {addressData.delAddress}
                        <br />
                        <span
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{
                            __html:
                              addressData.delCoAddress && `C/O ${addressData.delCoAddress}<br />`
                          }}
                        />
                        {addressData.delPostCode} {addressData.delPostAddress}
                        <br />
                        {addressData.country}
                      </p>
                    </Col>
                    <Col xs="12" sm="4">
                      <h4 className="bold">{t('order.invoice-address', 'Fakturerings-adress')}</h4>
                      <p>
                        {addressData.iName}
                        <br />
                        {addressData.invAddress}
                        <br />
                        {addressData.invCoAddress && `C/O ${addressData.invCoAddress}<br />`}
                        {addressData.invPostCode} {addressData.invPostAddress}
                        <br />
                        {addressData.country}
                      </p>
                    </Col>
                    <Col xs="12" sm="4">
                      <h4 className="bold">
                        {t('order.invoice-sent-to', 'Fakturan skickas till')}
                      </h4>
                      <p>
                        {addressData.invContactEmail}
                        {addressData.contactEmail !== addressData.iContactEmail && (
                          <>
                            <br />
                            {addressData.contactEmail}
                          </>
                        )}
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Button
                        color="secondary"
                        onClick={() => navigate('/golfclub-admin/club-information')}
                        className="float-right"
                      >
                        {t('order.information-wrong', 'Uppgifterna felaktiga')}
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </div>

            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">{t('order.order', 'Beställning')}</h3>
                </CardHeader>
                <CardBody>
                  {country === 'DK' && (
                    <Alert color="default">
                      Eftersom det ikke længere er tilladt for udenlandske virksomheder at have en
                      konto Danmark, skal alle indbetalinger derfor ske til vores svenske konto. Du
                      har mulighed for at vælge at blive faktureret i DKK eller EUR. Check med jeres
                      bank, hvad der er den billigste løsning for jer. Tøv ikke med at kontakte os,
                      hvis I har spørgsmål.
                    </Alert>
                  )}
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Row>
                      <Col xs="12" sm="4">
                        <FormGroup>
                          <Label>{t('order.select-product', 'Välj produkt')}</Label>
                          <Select
                            name="productCode"
                            register={register}
                            rules={{
                              required: true
                            }}
                            error={errors.productCode}
                          >
                            {products?.map((sp) => (
                              <option key={sp.code} value={sp.code}>
                                {sp.name}
                              </option>
                            ))}
                          </Select>
                        </FormGroup>
                      </Col>
                      <Col xs="12" sm="4">
                        <FormGroup>
                          <Label>{t('order.price', 'Pris')}</Label>
                          <Select
                            name="productPrice"
                            register={register}
                            rules={{
                              required: true
                            }}
                            error={errors.productPrice}
                          >
                            {country === 'DK' ? (
                              <>
                                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                                <option value="" />
                                <option value="EUR">EUR ({danishPrices.EUR} pr stk)</option>
                                <option value="DKK">DKK ({danishPrices.DKK} pr stk)</option>
                              </>
                            ) : (
                              <option value={product.price}>
                                {product.price} {product.currency}
                              </option>
                            )}
                            {isUserSuperAdmin &&
                              superAdminPriceOptions.map((o) => (
                                <option key={o} value={o}>{`${o} ${product.currency}`}</option>
                              ))}
                          </Select>
                        </FormGroup>
                      </Col>
                      <Col xs="12" sm="4">
                        <FormGroup>
                          <Label>{t('order.select-qty', 'Välj antal')}</Label>
                          <Select
                            name="productQty"
                            register={register}
                            rules={{
                              required: true
                            }}
                            error={errors.productQty}
                          >
                            <option value="">{t('order.select-qty', 'Välj antal')}</option>
                            {qtyOptions.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </Select>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs="12" sm="4">
                        <FormGroup>
                          <Label>{t('default.name', 'Namn')}</Label>
                          <GInput
                            name="name"
                            register={register}
                            rules={{
                              required: true,
                              minLength: 3
                            }}
                            error={errors.name}
                          />
                        </FormGroup>
                      </Col>
                      <Col xs="12" sm="4">
                        <FormGroup>
                          <Label>{t('default.email', 'E-post')}</Label>
                          <GInput
                            name="email"
                            type="email"
                            register={register}
                            rules={{ required: true, pattern: regexEmail }}
                            error={errors.email}
                          />
                        </FormGroup>
                      </Col>
                      <Col xs="12" sm="4">
                        <FormGroup>
                          <Label>{t('default.mobile', 'Mobilnummer')}</Label>
                          <GInput
                            name="mobile"
                            register={register}
                            rules={{
                              required: true,
                              minLength: 8
                            }}
                            error={errors.mobile}
                          />
                          <span className="small">
                            {t('order.mobilde-help', 'Används för avisering av paket')}
                          </span>
                        </FormGroup>
                      </Col>
                    </Row>
                    {isUserSuperAdmin && (
                      <>
                        <Row className="pt-1">
                          <Col xs="12" sm="4">
                            <FormGroup>
                              <Label>Planerad till</Label>
                              <GInput
                                name="planned"
                                type="date"
                                register={register}
                                rules={{ pattern: /^\d{4}-\d{2}-\d{2}$/ }}
                                error={errors.planned}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row className="pt-1">
                          <Col>
                            <FormGroup style={{ alignItems: 'center', display: 'flex' }}>
                              <GInput
                                name="onlyInvoice"
                                type="checkbox"
                                register={register}
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  display: 'inline-table',
                                  marginRight: '15px',
                                  color: '#8898aa',
                                  borderColor: '#dee2e6'
                                }}
                              />
                              Endast fakturering. Skicka INTE produkterna
                            </FormGroup>
                          </Col>
                        </Row>
                      </>
                    )}
                    <Row>
                      <Col>
                        <h4 className="bold">{t('order.other-products', 'Övriga paket')}</h4>
                        {isUserSuperAdmin && (
                          <p className="bold font-bold font-weight-bold">
                            OBS! Du kan inte lägga till gratis fysiska kort till en digital
                            beställning eller tvärtom. Det är alltså huvudbeställningen som avgör
                            korttypen.
                          </p>
                        )}
                        {extraProducts?.map((p) => (
                          <FormGroup key={p.code}>
                            <GInput
                              name={`x-${p.code}`}
                              type="number"
                              defaultValue={0}
                              register={register}
                              inline
                              style={{
                                width: '60px',
                                height: '40px',
                                display: 'inline-table',
                                marginRight: '15px'
                              }}
                            />
                            <Label className="small">{p.name}</Label>
                          </FormGroup>
                        ))}
                      </Col>
                    </Row>

                    {error && (
                      <Alert color="danger" className="mb-3">
                        {error}
                      </Alert>
                    )}
                    <Row>
                      <Col className="text-align-right">
                        <Button type="submit" color="success" disabled={!!inProgress}>
                          {inProgress === 'save' && <Spinner size="sm" className="mr-2" />}
                          {t('order.send-order', 'Skicka beställning')}
                        </Button>
                      </Col>
                    </Row>
                  </form>
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>

      {!!success && (
        <SweetAlerts
          type="success"
          title={t('order.thank-you', 'Tack för din beställning')}
          confirmBtnText={t('default.close')}
          onConfirm={clearAll}
          className="big"
        >
          <p className="small">
            {t(
              'order.received',
              // eslint-disable-next-line max-len
              'Din beställning är mottagen, vid digitala kort kommer dem vara tillgängliga för er inom 5 minuter. Fysiska kort skickas samma dag vid beställning innan 12.00 och dagen efter annars.'
            )}
          </p>
          {/* {campaign && (
            <Alert color="success">
              <ChatRightQuote style={{ fontSize: '20px' }} className="mr-2" />
              Korten har registreras på kampanjen <b>{campaign.name}</b>
            </Alert>
          )} */}
          <Table className="align-items-center mt-2" responsive>
            <thead className="thead-light">
              <tr>
                <th>{t('default.product')}</th>
                <th>{t('default.qty')}</th>
                <th>{t('order.a-price', 'À-pris')}</th>
              </tr>
            </thead>
            <tbody>
              {success.invoice?.items?.map((i) => (
                <tr key={i.product.id}>
                  <td>
                    {i.product.code} - {i.product.name}
                  </td>
                  <td>{i.product.qty}</td>
                  <td>
                    {i.product.price} {i.product.currency}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </SweetAlerts>
      )}
    </>
  );
};

export default Order;
